import { getBuildingRoomGroupKey } from './sessionGroupingUtils';

/**
 * Pure timer math for session group countdown timers (the 90-minute discharge
 * and similar). Extracted from TestSessionView so the orchestrator shrinks and
 * the logic is unit-testable with an injectable clock.
 *
 * A `timer` is the per-group object stored in
 * `sessionStore.groupTimers[groupName]`:
 *   { startedAt, durationMs }
 *
 * Status is idle | running | complete only — pause is not part of the model
 * (AS/NZS 2293.2 discharge is continuous).
 *
 * The optional `now` argument exists only for deterministic tests and defaults
 * to Date.now().
 */
export function getTimerRemainingMs(timer, now = Date.now()) {
  const t = timer;
  if (!t?.startedAt) return null;
  const elapsed = now - t.startedAt;
  const remaining = t.durationMs - elapsed;
  return remaining <= 0 ? 0 : remaining;
}

export function getTimerStatus(timer, now = Date.now()) {
  const t = timer;
  if (!t?.startedAt) return 'idle';
  const remaining = getTimerRemainingMs(t, now);
  if (remaining <= 0) return 'complete';
  return 'running';
}

/**
 * Which timer applies to an asset, and whether it's usable to stamp a discharge
 * start time — the one "which timer applies to this asset" rule shared by
 * RecordTestModal's exit/emergency submit and useTestSession.handleQuickPass
 * (Brief 18 §3). Do not duplicate this lookup: the modal and quick-pass paths
 * diverged for years because the guard only lived in the modal.
 *
 * Guards, both required:
 *  - a timer must be running for this asset's building/room group
 *  - that timer's siteId must match the current session's site — a stale
 *    timer carried over from another site must never stamp a start time.
 *
 * Returns an ISO string, or null when nothing should be stamped (no running
 * timer for this group, or a site mismatch).
 */
export function getDischargeStartTimeIso(asset, groupTimers, siteId) {
  const groupKey = getBuildingRoomGroupKey(asset);
  const groupTimer = groupTimers?.[groupKey];
  if (groupTimer?.startedAt != null && String(groupTimer.siteId) === String(siteId)) {
    return new Date(groupTimer.startedAt).toISOString();
  }
  return null;
}
